/**
 * Shared proficiency matching utilities for staff skill evaluation.
 *
 * Canonical scale (epic #3232): guided < independent < experienced < can_teach.
 * Legacy beginner/intermediate/expert/interest values are accepted. "Wants to
 * learn" (`interest` / a null level) never satisfies a requirement.
 */

import { fromAnyLevel, meetsMinimum } from './skillLevels';

/**
 * Rank map kept for callers that still index the scale. `interest` sits below
 * every real level; canonical and legacy keys share a rank.
 */
export const PROFICIENCY_ORDER: Record<string, number> = {
  interest: -1,
  beginner: 0,
  guided: 0,
  intermediate: 1,
  independent: 1,
  expert: 2,
  experienced: 2,
  can_teach: 3,
};

/** A held skill, as much of it as matching needs. */
export interface MechanicSkillLike {
  proficiencyLevel?: string | null;
  level?: string | null;
  wantsToLearn?: boolean;
  /** Certification expiry; missing/null means it never expires. */
  expiresAt?: string | null;
}

/** A skill requirement coming from a service or booking. */
export interface SkillRequirementLike {
  minimumProficiency?: string | null;
  /** When true, an expired certificate disqualifies the holder. */
  requiresValidCertificate?: boolean;
}

/**
 * True when `actual` proficiency is at least `minimum`. Wants-only / unknown
 * never meets a real requirement.
 */
export function meetsMinProficiency(
  actual: string | null | undefined,
  minimum: string | null | undefined,
): boolean {
  return meetsMinimum(actual, minimum);
}

/**
 * True when a certification has not expired. A missing/null `expiresAt` means the
 * skill never expires and is always valid.
 */
export function isCertValid(
  expiresAt: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!expiresAt) return true;
  const expiry = new Date(expiresAt);
  return Number.isNaN(expiry.getTime()) ? true : expiry.getTime() > now.getTime();
}

function heldLevel(skill: MechanicSkillLike): string | null {
  if (skill.level !== undefined && skill.level !== null) {
    return fromAnyLevel(skill.level);
  }
  return fromAnyLevel(skill.proficiencyLevel);
}

/**
 * True when a held skill qualifies for a requirement: proficiency meets the
 * minimum, and — only when the requirement asks for a valid certificate —
 * the certification has not expired.
 */
export function isQualifiedFor(
  skill: MechanicSkillLike,
  requirement: SkillRequirementLike,
  now: Date = new Date(),
): boolean {
  if (!meetsMinProficiency(heldLevel(skill), requirement.minimumProficiency ?? 'guided')) {
    return false;
  }
  if (requirement.requiresValidCertificate && !isCertValid(skill.expiresAt, now)) {
    return false;
  }
  return true;
}

/** A skill a person holds — enough of it for eligibility matching. */
export interface HeldSkill extends MechanicSkillLike {
  skillId: string;
}

/** A required skill on a service / booking. */
export interface RequiredSkill extends SkillRequirementLike {
  skillId: string;
}

/**
 * True when the person is qualified for EVERY required skill (each satisfied by
 * one of their held skills).
 */
export function isMechanicEligible(
  mechanic: { skills: HeldSkill[] },
  requiredSkills: RequiredSkill[],
  now: Date = new Date(),
): boolean {
  return requiredSkills.every((req) =>
    mechanic.skills.some((s) => s.skillId === req.skillId && isQualifiedFor(s, req, now)),
  );
}

/**
 * Partition people into those eligible for ALL required skills and the rest.
 * With no requirements, everyone is eligible.
 */
export function splitByEligibility<T extends { skills: HeldSkill[] }>(
  mechanics: T[],
  requiredSkills: RequiredSkill[],
  now: Date = new Date(),
): { eligible: T[]; ineligible: T[] } {
  if (requiredSkills.length === 0) return { eligible: [...mechanics], ineligible: [] };
  const eligible: T[] = [];
  const ineligible: T[] = [];
  for (const mechanic of mechanics) {
    if (isMechanicEligible(mechanic, requiredSkills, now)) eligible.push(mechanic);
    else ineligible.push(mechanic);
  }
  return { eligible, ineligible };
}
